// Purchase Orders tab: the pure part. Turns the job's live PO lines
// (readOpenCommitmentsCombinedPC → PC.v_Form_Open_PO_Items, every line of every
// OP/OL/OM/OS/OF order, received or not) and the per-line receipt/voucher rollup
// (readPOMatchStatus → dbo.Fact_PO_Receipt) into POs with their lines.
//
// This is the web form of the "PO Overview" page in the SC-PO Spend Power BI
// report, which reads the same JDE columns from bi_dw.v_PO_Detail:
//   Ordered  = Extended_Price_Amount  (+ Tax_Commitment_Amount)
//   Received = Received_Amount        (+ Tax_Relieved_Amount)
//   Open     = Open_Amount            (+ Tax_Commitment_Amount - Tax_Relieved_Amount)
// A line whose next status is 999 (complete) has nothing left to receive, so its
// open is 0 whatever JDE left in the column; PO_True_Open_Amount does the same.

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};
const EPS = 0.005;
const str = (v) => (v == null ? '' : String(v).trim());

export const PO_TYPES = {
    OP: 'Standard',
    OL: 'Equipment',
    OM: 'Material',
    OS: 'Subcontract',
    OF: 'Fleet',
};
export const typeLabel = (t) => PO_TYPES[t] || t || '';

// The receipt states. 'Closed' is a line JDE completed with less received than
// ordered (closed short, or cancelled before anything arrived).
export const RECEIPT_STATES = ['Not received', 'Partly received', 'Received', 'Closed'];

export const receiptState = ({ ordered, received, open, closed }) => {
    if (Math.abs(open) > EPS) return Math.abs(received) > EPS ? 'Partly received' : 'Not received';
    if (Math.abs(received) > EPS && Math.abs(received - ordered) <= Math.max(EPS, Math.abs(ordered) * 0.0005)) return 'Received';
    if (closed) return 'Closed';
    return Math.abs(received) > EPS ? 'Received' : 'Not received';
};

// readPOMatchStatus's words, in the terms the receiving and AP side use.
export const VOUCHER_LABEL = {
    'Vouchered': 'Vouchered',
    'Partially Vouchered': 'Partly vouchered',
    'Received': 'Not vouchered',
    'Reversed': 'Reversed',
};

export const matchKey = (po, line) => `${num(po)}|${num(line)}`;

export const indexMatchStatus = (rows) => {
    const m = new Map();
    (rows || []).forEach((r) => m.set(matchKey(r.PO_Number, r.PO_Line_Number), r));
    return m;
};

const dateOnly = (d) => (d ? String(d).slice(0, 10) : '');
const maxDate = (a, b) => (!a ? b : !b ? a : (a > b ? a : b));
const minDate = (a, b) => (!a ? b : !b ? a : (a < b ? a : b));

// One PO line. Amounts are kept both before and with tax so the tax switch is a
// pick, not a recompute.
export const buildLine = (r, match) => {
    const closed = str(r.Status_Code_Next) === '999';
    const base = {
        ordered: num(r.PO_Ordered_Amount),
        received: num(r.PO_Received_Amount),
        open: closed ? 0 : num(r.PO_Open_Amount),
    };
    const tax = {
        ordered: num(r.PO_Tax_Commitment_Amount),
        received: num(r.PO_Tax_Relieved_Amount),
        open: closed ? 0 : num(r.PO_Tax_Commitment_Amount) - num(r.PO_Tax_Relieved_Amount),
    };
    const qtyOrdered = num(r.Ordered_Quantity);
    return {
        key: `${str(r.PO_Company_ID)}|${str(r.PO_Type)}|${num(r.PO_Number)}|${num(r.PO_Line_Number)}`,
        poNumber: num(r.PO_Number),
        lineNumber: num(r.PO_Line_Number),
        description: str(r.PO_Line_Description),
        description2: str(r.PO_Line_Description_2),
        costType: str(r.Cost_Type_Code),
        costTypeDesc: str(r.Cost_Type_Description),
        phase: str(r.Phase_Code),
        phaseDesc: str(r.Phase_Code_Description),
        uom: str(r.Unit_Of_Measure),
        qtyOrdered,
        qtyReceived: num(r.Ordered_Quantity_Received),
        qtyOpen: closed ? 0 : num(r.Ordered_Quantity_Open),
        unitPrice: num(r.Ordered_Unit_Price),
        base,
        tax,
        closed,
        statusNext: str(r.Status_Code_Next),
        statusNextDesc: str(r.Status_Code_Next_Description),
        orderDate: dateOnly(r.Order_Date),
        promisedDate: dateOnly(r.Promised_Shipment_Date),
        rentalStart: dateOnly(r.Rental_Start_Date),
        rentalEnd: dateOnly(r.Rental_End_Date),
        lastReceived: dateOnly(match?.Last_Received_Date),
        notVouchered: num(match?.Open_Receipt_Amount),
        voucher: match ? (VOUCHER_LABEL[match.Match_Status] || match.Match_Status || '') : '',
        invoiceNumber: str(match?.Latest_Supplier_Invoice_Number),
        tapUrl: str(match?.TAP_URL),
        lastModified: r.Last_Modified_DateTime || null,
        state: receiptState({ ...base, closed }),
    };
};

const addTo = (acc, l) => {
    ['ordered', 'received', 'open'].forEach((k) => {
        acc.base[k] += l.base[k];
        acc.tax[k] += l.tax[k];
    });
    acc.notVouchered += l.notVouchered;
};

// Group lines into POs, newest order first. A PO is keyed on company + type +
// number: the same number can exist under two order types.
export const buildPurchaseOrders = (lineRows, matchRows) => {
    const match = indexMatchStatus(matchRows);
    // The receipt rollup is keyed on PO number + line only. Where two orders on
    // the job share that number under different types or companies, its row
    // could belong to either, so neither gets it rather than the wrong one.
    const owners = new Map();
    (lineRows || []).forEach((r) => {
        const k = matchKey(r.PO_Number, r.PO_Line_Number);
        if (!owners.has(k)) owners.set(k, new Set());
        owners.get(k).add(`${str(r.PO_Company_ID)}|${str(r.PO_Type)}`);
    });
    const byPo = new Map();
    (lineRows || []).forEach((r) => {
        const k = matchKey(r.PO_Number, r.PO_Line_Number);
        const line = buildLine(r, owners.get(k).size === 1 ? match.get(k) : undefined);
        const key = `${str(r.PO_Company_ID)}|${str(r.PO_Type)}|${num(r.PO_Number)}`;
        let po = byPo.get(key);
        if (!po) {
            po = {
                key,
                poNumber: num(r.PO_Number),
                company: str(r.PO_Company_ID),
                type: str(r.PO_Type),
                typeDesc: str(r.PO_Type_Description),
                vendor: str(r.Supplier_AB_Name),
                vendorNumber: str(r.Supplier_Number),
                orderDate: '',
                lastReceived: '',
                lines: [],
                base: { ordered: 0, received: 0, open: 0 },
                tax: { ordered: 0, received: 0, open: 0 },
                notVouchered: 0,
            };
            byPo.set(key, po);
        }
        po.lines.push(line);
        addTo(po, line);
        po.orderDate = minDate(po.orderDate, line.orderDate);
        po.lastReceived = maxDate(po.lastReceived, line.lastReceived);
    });
    const pos = [...byPo.values()];
    pos.forEach((po) => {
        po.lines.sort((a, b) => a.lineNumber - b.lineNumber);
        po.closed = po.lines.every((l) => l.closed);
        po.state = receiptState({ ...po.base, closed: po.closed });
        po.costTypes = [...new Set(po.lines.map((l) => l.costType).filter(Boolean))];
    });
    pos.sort((a, b) => (b.orderDate || '').localeCompare(a.orderDate || '') || b.poNumber - a.poNumber);
    return pos;
};

// The amounts a row shows, with or without sales tax.
export const amounts = (row, withTax) => (withTax
    ? {
        ordered: row.base.ordered + row.tax.ordered,
        received: row.base.received + row.tax.received,
        open: row.base.open + row.tax.open,
    }
    : { ...row.base });

export const pctReceived = (row) => {
    const { ordered, received } = row.base;
    if (Math.abs(ordered) > EPS) return received / ordered;
    return Math.abs(received) > EPS ? 1 : 0;
};

export const totalsOf = (pos, withTax) => pos.reduce((t, po) => {
    const a = amounts(po, withTax);
    t.ordered += a.ordered;
    t.received += a.received;
    t.open += a.open;
    t.notVouchered += po.notVouchered;
    t.lines += po.lines.length;
    t.pos += 1;
    return t;
}, { ordered: 0, received: 0, open: 0, notVouchered: 0, lines: 0, pos: 0 });

const haystack = (po) => [
    po.poNumber, po.vendor, po.vendorNumber, typeLabel(po.type), po.typeDesc,
    ...po.lines.flatMap((l) => [l.description, l.description2, l.costType, l.costTypeDesc, l.phase, l.phaseDesc, l.invoiceNumber]),
].join(' ').toLowerCase();

// Filters work on POs: a PO stays when it matches the search and its own
// receipt state is one of the picked ones.
export const filterPurchaseOrders = (pos, { search = '', type = 'All', state = 'All' } = {}) => {
    const q = search.trim().toLowerCase();
    return pos.filter((po) => (type === 'All' || po.type === type)
        && (state === 'All' || po.state === state)
        && (!q || haystack(po).includes(q)));
};

const SORTERS = {
    po: (a) => a.poNumber,
    type: (a) => typeLabel(a.type),
    vendor: (a) => a.vendor.toLowerCase(),
    orderDate: (a) => a.orderDate || '',
    lines: (a) => a.lines.length,
    ordered: (a, withTax) => amounts(a, withTax).ordered,
    received: (a, withTax) => amounts(a, withTax).received,
    open: (a, withTax) => amounts(a, withTax).open,
    pct: (a) => pctReceived(a),
    lastReceived: (a) => a.lastReceived || '',
    state: (a) => RECEIPT_STATES.indexOf(a.state),
};

export const sortPurchaseOrders = (pos, col, dir, withTax) => {
    const get = SORTERS[col];
    if (!get) return pos;
    const sign = dir === 'asc' ? 1 : -1;
    return [...pos].sort((a, b) => {
        const x = get(a, withTax);
        const y = get(b, withTax);
        if (x < y) return -sign;
        if (x > y) return sign;
        return b.poNumber - a.poNumber;
    });
};

export const countBy = (pos, field) => pos.reduce((m, po) => {
    m[po[field]] = (m[po[field]] || 0) + 1;
    return m;
}, {});

// Rows for the Excel export: one per line, with its PO's fields beside it.
export const exportRows = (pos, withTax) => pos.flatMap((po) => po.lines.map((l) => {
    const a = amounts(l, withTax);
    return {
        'PO #': po.poNumber,
        'PO type': `${po.type} ${typeLabel(po.type)}`,
        'Vendor #': po.vendorNumber,
        'Vendor': po.vendor,
        'Order date': l.orderDate,
        'Line': l.lineNumber,
        'Description': l.description,
        'Description 2': l.description2,
        'Cost type': l.costType,
        'Cost type description': l.costTypeDesc,
        'Phase': l.phase,
        'Phase description': l.phaseDesc,
        'UoM': l.uom,
        'Qty ordered': l.qtyOrdered,
        'Qty received': l.qtyReceived,
        'Qty open': l.qtyOpen,
        'Unit price': l.unitPrice,
        [withTax ? 'Ordered (with tax)' : 'Ordered']: a.ordered,
        [withTax ? 'Received (with tax)' : 'Received']: a.received,
        [withTax ? 'Open (with tax)' : 'Open']: a.open,
        'Tax ordered': l.tax.ordered,
        'Tax received': l.tax.received,
        'Promised date': l.promisedDate,
        'Rental start': l.rentalStart,
        'Rental end': l.rentalEnd,
        'Last received': l.lastReceived,
        'Received, not vouchered': l.notVouchered,
        'Voucher': l.voucher,
        'Supplier invoice': l.invoiceNumber,
        'Receipt status': l.state,
        'JDE next status': `${l.statusNext} ${l.statusNextDesc}`.trim(),
    };
}));
